
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { requestSubscriptionStatus } from '@/lib/subscription-status-request';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { ENVIRONMENT } from '@/lib/environment';
import { AuthSecurityValidator } from '@/lib/auth-security';
import { UnifiedAuthContextType, UnifiedAuthProviderProps } from './types';
import { useAuthActions } from './auth-hooks';
import { logger } from '@/lib/logger';
import { invalidateVerifyAccessForUser } from '@/lib/verify-access-cache';
import { queryClient } from '@/lib/query-client';
import { coordinatedAuthRefresh } from '@/lib/auth-query-coordinator';
import { ensureFreshAuth, subscribeEnsureFreshAuth, type EnsureFreshAuthResult, verifyUserNetwork } from '@/lib/auth-readiness';
import { appendAuthDebugLog } from '@/lib/auth-debug';
import { setRefreshPromise, clearRefreshPromise } from '@/lib/auth-refresh-gate';
import { createRefocusScheduler } from './refocus-scheduler';
import { keepUserIfSame, keepSessionIfSame } from './auth-identity';

// Self-heal: ensure CSRF token exists in user user_metadata after login (app_metadata fallback is legacy)
async function ensureCsrfTokenIfMissing(): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const hasUser = !!session?.user;
    const currentUserMeta = session?.user?.user_metadata?.csrf_token as string | undefined;
    const legacyAppMeta = session?.user?.app_metadata?.csrf_token as string | undefined;
    if (!hasUser) {
      return;
    }
    // Only self-heal when token is missing from user_metadata AND not present in legacy app_metadata
    if (!currentUserMeta && !legacyAppMeta) {
      const newToken = crypto.randomUUID();
      await supabase.auth.updateUser({ data: { csrf_token: newToken } });
      // Force a local session refresh so subsequent requests immediately see the token
      await supabase.auth.refreshSession();
      logger.info('✅ CSRF token self-healed for current user');
      // Do not force refresh here; rely on auth state change event to propagate
    }
  } catch (e) {
    logger.error('CSRF self-heal failed', e);
  }
}

const UnifiedAuthContext = React.createContext<UnifiedAuthContextType>({
  user: null,
  session: null,
  loading: true,
  subscriptionTier: 'free',
  isSubscribed: false,
  signIn: async () => ({ error: null, data: null }),
  signUp: async () => ({ error: null, data: null }),
  signInWithGoogle: async () => ({ error: null, data: null }),
  signOut: async () => {},
  resetPasswordForEmail: async () => ({ error: null, data: null }),
  validateAccess: () => false,
});

export const useUnifiedAuth = () => {
  const context = React.useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error('useUnifiedAuth must be used within UnifiedAuthProvider');
  }
  
  return context;
};

/**
 * SECURITY: Server-side subscription validation using Edge Function
 * This ensures subscription status cannot be manipulated client-side
 * Launch contract: on transient failures, preserve the last known tier rather
 * than downgrading the UI. Server checks remain the authority for premium gates.
 */
const fetchUserSubscriptionData = async (userId: string): Promise<{ tier: string; isSubscribed: boolean }> => {
  logger.info('🔍 SUBSCRIPTION FETCH START', { userId });
  
  try {
    // Get current session (still log for diagnostics)
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    logger.debug('Session check for subscription', {
      hasSession: !!session,
      sessionUserId: session?.user?.id,
      hasAccessToken: !!session?.access_token,
      sessionErrorMessage: sessionError?.message,
      requestedUserId: userId,
      userIdMatch: session?.user?.id === userId
    });

    if (!session) {
      logger.warn('❌ No session available for subscription check - keeping previous state');
      // Return a sentinel that signals caller to keep existing state
      return { tier: 'keep', isSubscribed: false };
    }

    if (session.user.id !== userId) {
      logger.warn('⚠️ User ID mismatch in subscription fetch', {
        sessionUserId: session.user.id,
        requestedUserId: userId
      });
    }

    logger.debug('Making subscription API call (shared in-flight request)');
    // Shared with SubscriptionProvider: concurrent callers join one request.
    const response = await requestSubscriptionStatus(userId);
    
    logger.debug('Subscription API response', { 
      ok: response.ok,
      status: response.status, 
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    if (!response.ok) {
      let errorDetails;
      try {
        errorDetails = await response.text();
        logger.error('❌ Subscription API call failed', { 
          status: response.status, 
          statusText: response.statusText,
          errorBody: errorDetails
        });
      } catch (e) {
        logger.error('❌ Subscription API call failed (no error body)', { 
          status: response.status, 
          statusText: response.statusText
        });
      }
      // Preserve previous tier on transient backend/auth failures.
      return { tier: 'keep', isSubscribed: false };
    }
    
    // Narrow the response via DTO schema
    const raw = await response.json();
    const { getSubscriptionStatusResponseSchema } = await import('@/types/dto');
    const parse = getSubscriptionStatusResponseSchema.safeParse(raw);
    if (!parse.success) {
      logger.warn('⚠️ Subscription API returned invalid shape', { issues: parse.error.issues });
      return { tier: 'keep', isSubscribed: false };
    }
    const data = parse.data;
    logger.info('✅ SUBSCRIPTION FETCH SUCCESS', { 
      rawData: data,
      tier: data.subscriptionTier, 
      isSubscribed: data.isSubscribed,
      status: data.status,
      expiresAt: data.expiresAt,
      debug: data.debug
    });
    
    if (data) {
      // Enhanced subscription validation with debugging
      const result = { 
        tier: data.subscriptionTier || 'free', 
        isSubscribed: data.isSubscribed || false 
      };
      
      // Log detailed subscription analysis
      logger.info('🔍 SUBSCRIPTION ANALYSIS', {
        userId,
        rawResponse: data,
        computedResult: result,
        debug: data.debug,
        validation: {
          hasTier: !!data.subscriptionTier,
          hasIsSubscribed: typeof data.isSubscribed === 'boolean',
          tierValue: data.subscriptionTier,
          isSubscribedValue: data.isSubscribed,
          status: data.status,
          expiresAt: data.expiresAt
        }
      });
      
      // Additional validation: if we have a status but no tier, log it
      if (data.status && !data.subscriptionTier) {
        logger.warn('⚠️ Subscription has status but no tier', {
          userId,
          status: data.status,
          expiresAt: data.expiresAt
        });
      }
      
      // Additional validation: if we have a tier but it's not being recognized
      if (data.subscriptionTier && data.subscriptionTier !== result.tier) {
        logger.warn('⚠️ Tier mismatch detected', {
          userId,
          originalTier: data.subscriptionTier,
          computedTier: result.tier,
          isSubscribed: data.isSubscribed
        });
      }
      
      logger.info('🎯 FINAL SUBSCRIPTION RESULT', result);
      return result;
    }
    
    logger.warn('⚠️ No subscription data returned - keeping previous state');
    return { tier: 'keep', isSubscribed: false };
  } catch (error) {
    logger.error('💥 SUBSCRIPTION FETCH ERROR', { error, userId });
    // Signal caller to keep previous state on transient errors
    return { tier: 'keep', isSubscribed: false };
  }
};

export const UnifiedAuthProvider: React.FC<UnifiedAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authSettled, setAuthSettled] = useState(true);
  const [hardExpired, setHardExpired] = useState(false);
  const [softRecovering, setSoftRecovering] = useState(false);
  // Back-compat: keep sessionExpired mapped to hardExpired
  const sessionExpired = hardExpired;
  const [hasShownWelcome, setHasShownWelcome] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<string>('free');
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const navigate = useNavigate();
  const { signIn, signUp, signOut, resetPasswordForEmail, updateEmail, signInWithGoogle } = useAuthActions();
  const initializedRef = useRef(false);
  const prevUserIdRef = useRef<string | null>(null);
  const lastSubscriptionRefreshMsRef = useRef<number>(0);
  const subscriptionCacheRef = useRef<{ userId: string; tier: string; isSubscribed: boolean; ts: number } | null>(null);
  const inRefocusRef = useRef<boolean>(false);
  const refocusBusyUntilRef = useRef<number>(0);
  const refocusCycleIdRef = useRef<number>(0);
  const lastSuccessfulRefreshRef = useRef<number>(Date.now());
  const signingOutRef = useRef<boolean>(false);
  const lastRefocusCycleSeenRef = useRef<number>(0);
  // When the last refocus flow actually started. Mount counts: initializeAuth
  // has just read the session and subscription, so the load-time focus/pageshow
  // events do not need a second pass.
  const lastRefocusStartRef = useRef<number>(Date.now());

  // Memoized function to update subscription data with server-side verification
  const updateSubscriptionData = useCallback(async (user: User | null) => {
    logger.info('📊 UPDATE SUBSCRIPTION DATA CALLED', { 
      hasUser: !!user, 
      userId: user?.id,
      currentTier: subscriptionTier,
      currentIsSubscribed: isSubscribed
    });

    if (!user) {
      logger.debug('No user - setting subscription to free');
      setSubscriptionTier('free');
      setIsSubscribed(false);
      return;
    }

    try {
      // TTL cache: return cached result if same user within 30s
      const TTL_MS = 30_000;
      const cache = subscriptionCacheRef.current;
      if (cache && cache.userId === user.id && Date.now() - cache.ts < TTL_MS) {
        logger.debug('Using subscription TTL cache', { userId: user.id, ageMs: Date.now() - cache.ts });
        setSubscriptionTier(cache.tier);
        setIsSubscribed(cache.isSubscribed);
        return;
      }
      logger.debug('Calling fetchUserSubscriptionData...');
      const { tier, isSubscribed: newIsSubscribed } = await fetchUserSubscriptionData(user.id);

      // Only apply updates when we have a concrete tier (not sentinel "keep")
      if (tier !== 'keep') {
        logger.info('🔄 UPDATING SUBSCRIPTION STATE', {
          previousTier: subscriptionTier,
          newTier: tier,
          previousIsSubscribed: isSubscribed,
          newIsSubscribed
        });
        setSubscriptionTier(tier);
        setIsSubscribed(newIsSubscribed);
        subscriptionCacheRef.current = { userId: user.id, tier, isSubscribed: newIsSubscribed, ts: Date.now() };
        logger.info('✅ SUBSCRIPTION STATE UPDATED', { tier, isSubscribed: newIsSubscribed });
      } else {
        logger.info('ℹ️ Keeping previous subscription state due to transient condition', {
          previousTier: subscriptionTier,
          previousIsSubscribed: isSubscribed
        });
      }
    } catch (error) {
      // Keep previous state on error
      logger.error('❌ Error in updateSubscriptionData - preserving previous state', { error });
    }
  }, [subscriptionTier, isSubscribed]);

  // Debounced wrapper to avoid repeated refreshes on focus-driven auth events
  const maybeUpdateSubscriptionData = useCallback(async (currentUser: User | null) => {
    const now = Date.now();
    const last = lastSubscriptionRefreshMsRef.current || 0;
    const prevUserId = prevUserIdRef.current;
    const currentUserId = currentUser?.id || null;

    // Refresh immediately if user identity changed; otherwise debounce to 60s window
    const userChanged = prevUserId !== currentUserId;
    if (userChanged || now - last > 60_000) {
      await updateSubscriptionData(currentUser);
      lastSubscriptionRefreshMsRef.current = Date.now();
      prevUserIdRef.current = currentUserId;
    }
  }, [updateSubscriptionData]);

  // Initialize authentication
  const initializeAuth = async () => {
    try {
      // PRODUCTION: Enhanced security validation
      if (ENVIRONMENT.isProduction()) {
        logger.info('Initializing production authentication');
        
        // Validate user ID format if available
        if (user?.id) {
          const isValidUserId = AuthSecurityValidator.validateUserId(user.id);
          if (!isValidUserId) {
            logger.error('Invalid user ID format detected');
          }
        }
      }

      // Get initial session
      const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        const status = (sessionError as { status?: number } | undefined)?.status;
        logger.error('Error getting initial session', { error: sessionError.message, status });
      }

      if (initialSession?.user) {
        setUser(keepUserIfSame(initialSession.user));
        setSession(keepSessionIfSame(initialSession));
        // Self-heal CSRF for legacy users missing token
        await ensureCsrfTokenIfMissing();
        // Establish subscription state before clearing initial auth loading.
        prevUserIdRef.current = initialSession.user.id;
        lastSubscriptionRefreshMsRef.current = Date.now();
        await updateSubscriptionData(initialSession.user).catch((error) => {
          logger.error('Subscription update error (init):', error);
        });
      }

      // Ensure loading is cleared even if no auth state change event fires immediately
      setLoading(false);
      logger.debug('Auth initialization complete after initial session check, loading set to false');

      // Set up auth state listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          // Determine if this is a real transition (sign-in/out) vs passive refresh
          const nextUserId = session?.user?.id || null;
          const prevUserId = prevUserIdRef.current;
          const userChanged = prevUserId !== nextUserId;
          const isRealSignIn = event === 'SIGNED_IN' && userChanged;
          const isRealSignOut = event === 'SIGNED_OUT';
          const shouldBlock = isRealSignIn || isRealSignOut;
          if (shouldBlock) setLoading(true);
          logger.info('🔐 AUTH STATE CHANGE', { 
            event, 
            hasSession: !!session,
            hasUser: !!session?.user,
            userId: session?.user?.id,
            email: session?.user?.email
          });
          
          // Invalidate verify-access cache when user identity changes
          if (userChanged && prevUserId) {
            invalidateVerifyAccessForUser(prevUserId);
          }

          // Harden against transient null sessions during token refresh:
          // - Only clear user/session on explicit SIGNED_OUT
          // - Otherwise, preserve previous user/session to avoid disabling queries
          if (isRealSignOut) {
            setUser(null);
            setSession(null);
          } else if (session?.user) {
            setUser(keepUserIfSame(session.user));
            setSession(keepSessionIfSame(session));
          } else {
            // keep previous user/session; no-op
          }

          if (session?.user) {
            // Self-heal CSRF token if missing on auth transitions — but NOT
            // inside this callback. supabase-js holds its auth lock while an
            // onAuthStateChange callback runs, so awaiting getSession /
            // updateUser / refreshSession here waits on ourselves until the
            // 8s auth timeout (src/integrations/supabase/client.ts). Every
            // other auth call queues behind it. 03-10 production, a new
            // user's first session: "CSRF self-heal failed" and "SUBSCRIPTION
            // FETCH ERROR", both "auth.getSession timeout", plus the refocus
            // dead-man. Run it once the callback has returned.
            setTimeout(() => { void ensureCsrfTokenIfMissing(); }, 0);
            logger.debug('User found in auth state change - maybe updating subscription (debounced)');
            // Non-blocking, debounced subscription refresh
            maybeUpdateSubscriptionData(session.user).catch((error) => {
              logger.error('Subscription update error (auth change):', error);
            });
          } else {
            // Only downgrade to free on explicit sign-out events
            if (event === 'SIGNED_OUT') {
              logger.debug('Explicit sign-out detected - setting to free');
              setSubscriptionTier('free');
              setIsSubscribed(false);
              prevUserIdRef.current = null;
            } else {
              logger.debug('No user in auth state change, but not SIGNED_OUT - preserving previous state');
            }
          }
          
          if (shouldBlock) setLoading(false);
          logger.debug('Auth initialization complete, loading set to false');
        }
      );

      return () => subscription.unsubscribe();
    } catch (error) {
      logger.error('Error initializing auth:', error);
      setLoading(false);
    }
  };

  // Initialize auth on mount
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      initializeAuth();
    }
  }, []);

  // Refresh session and revalidate on visibility change to avoid stale auth after tab refocus
  useEffect(() => {
    // Phase 2 experiment flag
    const refocusEnv = (import.meta.env as any)?.VITE_RQ_EXPERIMENT_REFOCUS;
    const isExperimentRefocus =
      String(refocusEnv ?? '').toLowerCase() === 'true' || refocusEnv === '1';

    let channel: BroadcastChannel | null = null;
    let signoutChannel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel('auth-refocus');
    } catch (e) {
      logger.debug('BroadcastChannel unavailable for auth-refocus', { error: e instanceof Error ? e.message : String(e) });
      try { appendAuthDebugLog({ type: 'bc:init-failed', channel: 'auth-refocus' }); } catch { /* no-op */ }
    }
    try {
      signoutChannel = new BroadcastChannel('auth-signout');
    } catch (e) {
      logger.debug('BroadcastChannel unavailable for auth-signout', { error: e instanceof Error ? e.message : String(e) });
      try { appendAuthDebugLog({ type: 'bc:init-failed', channel: 'auth-signout' }); } catch { /* no-op */ }
    }

    const safePost = (payload: unknown) => {
      try { channel?.postMessage(payload as any); } catch (err) {
        logger.debug('BroadcastChannel postMessage failed', { error: err instanceof Error ? err.message : String(err) });
        try { appendAuthDebugLog({ type: 'bc:post-failed' }); } catch { /* no-op */ }
      }
    };

    const onChannelMessage = (e: MessageEvent) => {
      try {
        const data = e?.data as { type?: string; ts?: number; ok?: boolean; cycleId?: number } | undefined;
        if (!data?.type) return;
        if (typeof data.cycleId === 'number') {
          if (data.cycleId < lastRefocusCycleSeenRef.current) {
            logger.debug('Refocus broadcast ignored (stale cycle)', { cycleId: data.cycleId, seen: lastRefocusCycleSeenRef.current });
            return;
          }
          lastRefocusCycleSeenRef.current = data.cycleId;
        }
        if (data.type === 'refocus:start') {
          refocusBusyUntilRef.current = Date.now() + 2000;
          logger.debug('Refocus broadcast start received', { cycleId: data.cycleId, busyUntil: refocusBusyUntilRef.current });
        }
        if (data.type === 'refocus:end' && data.ok === false) {
          logger.warn('Refocus broadcast end reported failure', { cycleId: data.cycleId, reason: (data as { reason?: string }).reason });
        }
      } catch (_error) {
        // no-op
      }
    };
    channel?.addEventListener('message', onChannelMessage);
    const onSignoutMessage = (e: MessageEvent) => {
      try {
        const data = e?.data as { type?: string } | undefined;
        if (data?.type === 'signout:start') {
          signingOutRef.current = true;
        }
        if (data?.type === 'signout:end') {
          signingOutRef.current = false;
        }
      } catch (_error) {
        // no-op
      }
    };
    signoutChannel?.addEventListener('message', onSignoutMessage);

    // Shared predicate for auth-gated queries that need refetch after refocus
    const isAuthGatedQuery = (q: any): boolean => {
      const key = ((q as { queryKey?: readonly unknown[] }).queryKey ?? []) as readonly unknown[];
      if (!Array.isArray(key) || key.length === 0) return false;
      const root = key[0];
      const includesPersonas = (key as unknown[]).includes('personas');
      const startsWithAuthGated = typeof root === 'string' && (
        root.startsWith('supervision') ||
        root.startsWith('ethics') ||
        root.startsWith('dashboard') ||
        root.startsWith('session') ||
        root.startsWith('thread') ||
        root.startsWith('technique') ||
        root.startsWith('mentor') ||
        root.startsWith('sessions') ||
        root.startsWith('threads') ||
        root.startsWith('user-profile')
      );
      return includesPersonas || startsWithAuthGated;
    };

    // Immediate invalidation + refetch after refocus completes
    // This ensures fresh data is available when queries re-enable
    const immediatePostRefreshRefetch = async () => {
      try {
        // First invalidate to mark as stale
        await queryClient.invalidateQueries({
          predicate: isAuthGatedQuery
        });
        // Then immediately refetch to force fresh data, including inactive queries
        await queryClient.refetchQueries({
          predicate: isAuthGatedQuery,
          type: 'all'
        });
        appendAuthDebugLog({ type: 'refocus:immediate-refetch' });
      } catch (error) {
        logger.debug('Refocus flow: immediate refetch failed', { 
          reason: 'non-fatal', 
          error: error instanceof Error ? error.message : String(error) 
        });
      }
    };

    const runRefocusFlow = async (trigger: 'visibility' | 'focus' | 'pageshow' | 'watchdog' = 'visibility') => {
    const cycleId = ++refocusCycleIdRef.current;
    if (signingOutRef.current) {
      try { appendAuthDebugLog({ type: 'runRefocusFlow:aborted-signout', cycleId, trigger }); } catch { /* no-op */ }
      logger.debug('Refocus flow aborted: signing out', { cycleId, trigger });
      return;
    }
    if (inRefocusRef.current) return;
    if (Date.now() < refocusBusyUntilRef.current) return;
    lastRefocusStartRef.current = Date.now();

    const MAX_ATTEMPTS = 3;
    const BASE_DELAY_MS = 350;

    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const withTimeout = async <T,>(promise: Promise<T>, ms: number): Promise<T | 'timeout'> => {
      return new Promise<T | 'timeout'>((resolve) => {
        const timer = setTimeout(() => resolve('timeout'), ms);
        promise.then((value) => { clearTimeout(timer); resolve(value); })
               .catch(() => { clearTimeout(timer); resolve('timeout'); });
      });
    };

    // Removed auth-echo probe to reduce parallel auth traffic

    // Watchdog: if refocus does not settle within 3s, log slow state
    let watchdogId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      try {
        // Do not mark expired here; just log a slow refocus hint
        appendAuthDebugLog({ type: 'runRefocusFlow:watchdog-slow', cycleId });
        try { window.dispatchEvent(new CustomEvent('auth:refocus:slow', { detail: { cycleId, ts: Date.now() } })); } catch { /* no-op */ }
      } catch {
        // no-op
      }
    }, 3000);

    // Dead-man fail-safe: force a settle after 8s to prevent indefinite pending
    let deadmanId: ReturnType<typeof setTimeout> | null = setTimeout(async () => {
      try {
        if (!inRefocusRef.current) return;
        appendAuthDebugLog({ type: 'runRefocusFlow:deadman', cycleId });
        logger.warn('Refocus flow: dead-man fail-safe triggered', { cycleId });
        setSoftRecovering(true);
        setHardExpired(false);
        try { window.dispatchEvent(new CustomEvent('auth:refocus:failure', { detail: { cycleId, reason: 'deadman', ts: Date.now() } })); } catch { /* no-op */ }
        // Refetch queries before enabling them
        await immediatePostRefreshRefetch();
      } finally {
        inRefocusRef.current = false;
        setAuthSettled(true);
      }
    }, 8000);

    type AttemptResult = {
      outcome: 'success' | 'expired' | 'transient_failure';
      reason?: string;
    };

    const attemptRefocus = async (attemptLabel: number): Promise<AttemptResult> => {
      // Immediate rehydration from persisted session
      try {
        const { data: { session: preSession } } = await supabase.auth.getSession();
        if (preSession?.user) {
          setUser(keepUserIfSame(preSession.user));
          setSession(keepSessionIfSame(preSession));
          logger.debug('Refocus flow: rehydrated from persisted session', { userId: preSession.user.id });
        }
      } catch (err) {
        logger.debug('Refocus flow: pre-session rehydration skipped', { reason: err instanceof Error ? err.message : 'no-session' });
      }

      // Always perform a refresh on refocus to avoid races
      // (This keeps the window for stale headers ~0)

      // Publish refresh promise so callers can coalesce with refocus
      const refreshPromise = ensureFreshAuth({ source: `runRefocusFlow:${trigger}:attempt-${attemptLabel}` });
      try { setRefreshPromise(refreshPromise); } catch { /* no-op */ }
      const refreshResultOrTimeout = await withTimeout(refreshPromise, 1500);
      try { clearRefreshPromise(); } catch { /* no-op */ }
      if (refreshResultOrTimeout === 'timeout') {
        appendAuthDebugLog({ type: 'runRefocusFlow:ensureFreshAuth-timeout', cycleId, attempt: attemptLabel });
        // Fallback: verify via network; if valid, proceed as success
        const verified = await verifyUserNetwork();
        appendAuthDebugLog({ type: 'runRefocusFlow:network-fallback', cycleId, attempt: attemptLabel, ok: verified });
        if (verified) {
          try {
            const { data: { session: currentVerified } } = await supabase.auth.getSession();
            if (currentVerified?.user) {
              setUser(keepUserIfSame(currentVerified.user));
              setSession(keepSessionIfSame(currentVerified));
              await ensureCsrfTokenIfMissing();
              await maybeUpdateSubscriptionData(currentVerified.user);
              lastSuccessfulRefreshRef.current = Date.now();
              return { outcome: 'success' as const };
            }
          } catch {
            // fall through to transient failure
          }
          return { outcome: 'transient_failure' as const, reason: 'network_fallback_no_user' };
        }
        return { outcome: 'transient_failure' as const, reason: 'ensureFreshAuth_timeout' };
      }
      const refreshResult = refreshResultOrTimeout;
      logger.info('Refocus flow: ensureFreshAuth completed', { refreshResult });
      appendAuthDebugLog({ type: 'runRefocusFlow:ensureFreshAuth', cycleId, attempt: attemptLabel, refreshResult });

      if (!refreshResult.ok) {
        const reason = refreshResult.reason;
        const isExpired = reason === 'no_session';
        return { outcome: isExpired ? 'expired' : 'transient_failure', reason };
      }

      const { data: { session: current } } = await supabase.auth.getSession();
      if (!current?.user) {
        logger.warn('Refocus flow: refresh succeeded but no user present');
        return { outcome: 'transient_failure', reason: 'no_user_after_refresh' };
      }

      setUser(keepUserIfSame(current.user));
      setSession(keepSessionIfSame(current));
      await ensureCsrfTokenIfMissing();
      // Debounced (60s; immediate on user change): a quick return does not refetch.
      await maybeUpdateSubscriptionData(current.user);
      lastSuccessfulRefreshRef.current = Date.now();

      // Note: immediatePostRefreshRefetch is called in the success path before enabling queries

      return { outcome: 'success' };
    };

    inRefocusRef.current = true;
    setAuthSettled(false);
    safePost({ type: 'refocus:start', ts: Date.now(), cycleId, trigger });
    try {
      const meta: Record<string, unknown> = { cycleId, trigger };
      await import('@/lib/sentry-config').then(m => m.captureMessage('auth_refocus_start', 'info', meta)).catch(() => {});
    } catch (e) { /* no-op */ }

    let finalOutcome: AttemptResult = { outcome: 'transient_failure', reason: 'not_attempted' };

    try {
      appendAuthDebugLog({ type: 'runRefocusFlow:start', cycleId, trigger, prevUserId: user?.id });
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const attemptLabel = attempt + 1;
        logger.debug('Refocus flow: attempt', { attempt: attemptLabel, cycleId, trigger });
        appendAuthDebugLog({ type: 'runRefocusFlow:attempt', cycleId, attempt: attemptLabel });
        try {
          finalOutcome = await attemptRefocus(attemptLabel);
          appendAuthDebugLog({ type: 'runRefocusFlow:attempt-result', cycleId, attempt: attemptLabel, result: finalOutcome });
        } catch (err) {
          logger.warn('Refocus flow: attempt encountered error', { attempt: attemptLabel, error: err instanceof Error ? err.message : String(err) });
          finalOutcome = { outcome: 'transient_failure', reason: err instanceof Error ? err.message : 'unknown_error' };
          appendAuthDebugLog({ type: 'runRefocusFlow:attempt-error', cycleId, attempt: attemptLabel, error: err instanceof Error ? err.message : String(err) });
        }

        if (finalOutcome.outcome === 'success') break;
        if (finalOutcome.outcome === 'expired') break;

        const backoff = BASE_DELAY_MS * Math.pow(2, attempt);
        logger.debug('Refocus flow: scheduling retry', { attempt: attemptLabel, backoff });
        await wait(backoff + Math.random() * 200);
      }

      if (finalOutcome.outcome === 'success') {
        if (watchdogId) { clearTimeout(watchdogId); watchdogId = null; }
        if (deadmanId) { clearTimeout(deadmanId); deadmanId = null; }
        setSoftRecovering(false);
        setHardExpired(false);
        safePost({ type: 'refocus:end', ts: Date.now(), ok: true, cycleId });
        appendAuthDebugLog({ type: 'runRefocusFlow:success', cycleId, userId: (await supabase.auth.getUser()).data?.user?.id });
        try {
          const meta: Record<string, unknown> = { cycleId };
          await import('@/lib/sentry-config').then(m => m.captureMessage('auth_refocus_success', 'info', meta)).catch(() => {});
        } catch (e) { /* no-op */ }
        try { window.dispatchEvent(new CustomEvent('auth:refocus:success', { detail: { cycleId, ts: Date.now() } })); } catch { /* no-op */ }

        // CRITICAL: Refetch queries BEFORE enabling them
        await immediatePostRefreshRefetch();
        return;
      }

      const reason = finalOutcome.reason || 'unknown_failure';
      if (finalOutcome.outcome === 'expired') {
        logger.warn('Refocus flow: session considered expired after attempts', { cycleId, reason });
      } else {
        logger.warn('Refocus flow: transient failures exhausted attempts', { cycleId, reason });
      }
      // Only mark hard expired on definitive, verified no session; else soft recovering
      if (reason === 'no_session') {
        try {
          const quick = await supabase.auth.getUser();
          const stillHasUser = !!quick.data?.user?.id;
          appendAuthDebugLog({ type: 'runRefocusFlow:verify-no_session', cycleId, stillHasUser, quickUserId: quick.data?.user?.id });
          if (stillHasUser) {
            setSoftRecovering(true);
          } else {
            setHardExpired(true);
            setSoftRecovering(false);
          }
        } catch {
          setHardExpired(true);
          setSoftRecovering(false);
        }
      } else {
        setSoftRecovering(true);
      }
      safePost({ type: 'refocus:end', ts: Date.now(), ok: false, cycleId, reason });
      appendAuthDebugLog({ type: 'runRefocusFlow:failure', cycleId, reason });
      try {
        const meta: Record<string, unknown> = { cycleId, reason };
        await import('@/lib/sentry-config').then(m => m.captureMessage('auth_refocus_failure', 'warning', meta)).catch(() => {});
      } catch (e) { /* no-op */ }
      try { window.dispatchEvent(new CustomEvent('auth:refocus:failure', { detail: { cycleId, reason, ts: Date.now() } })); } catch { /* no-op */ }
      // Fallback refetch to unblock views even on failure
      await immediatePostRefreshRefetch();
    } finally {
        if (watchdogId) { clearTimeout(watchdogId); watchdogId = null; }
        if (deadmanId) { clearTimeout(deadmanId); deadmanId = null; }
        setAuthSettled(true);
        inRefocusRef.current = false;
        // Post-enable: ensure active queries refetch after queries become enabled
        try {
          setTimeout(() => {
            try {
              queryClient.refetchQueries({ predicate: isAuthGatedQuery, type: 'active' });
            } catch {
              // ignore
            }
          }, 0);
        } catch {
          // ignore
        }
      appendAuthDebugLog({ type: 'runRefocusFlow:finally', cycleId, authSettled: true });
      }
    };

    // visibilitychange -> visible, window focus and pageshow are "the user came
    // back"; together they collapse into one debounced, rate-limited run.
    // focusin is NOT one of them: it fires on every click into a textarea or
    // button inside the page and drove a refocus cycle every few seconds while
    // a user typed (03-10 production).
    const scheduler = createRefocusScheduler({
      run: (trigger) => { void runRefocusFlow(trigger); },
      getLastStartMs: () => lastRefocusStartRef.current,
      onSkip: (trigger, sinceLastMs) => {
        try { appendAuthDebugLog({ type: 'refocus:skipped-recent', trigger, sinceLastMs }); } catch { /* no-op */ }
      },
    });

    const onVisibility = () => {
      if (import.meta.env.DEV) {
        try {
          logger.info('VISIBILITY_CHANGE', { state: document.visibilityState, ts: Date.now() });
        } catch {
          // no-op
        }
      }
      if (!isExperimentRefocus && document.visibilityState === 'visible') {
        scheduler.trigger('visibility');
      }
    };
    const onFocus = () => {
      if (import.meta.env.DEV) {
        try { logger.info('FOCUS', { ts: Date.now() }); } catch { /* no-op */ }
      }
      if (!isExperimentRefocus) {
        scheduler.trigger('focus');
      }
    };
    const onPageShow = (e: Event) => {
      if (import.meta.env.DEV) {
        const persisted = (e as { persisted?: boolean } | undefined)?.persisted ?? false;
        try { logger.info('PAGESHOW_BFCACHE', { persisted, ts: Date.now() }); } catch { /* no-op */ }
      }
      if (!isExperimentRefocus) {
        scheduler.trigger('pageshow');
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    try { appendAuthDebugLog({ type: 'refocus:listeners-installed', visibility: true, focus: true, pageshow: true, bc: Boolean(channel) }); } catch { /* no-op */ }
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      scheduler.cancel();
      if (channel) {
        channel.removeEventListener('message', onChannelMessage);
        channel.close();
      }
      if (signoutChannel) {
        signoutChannel.removeEventListener('message', onSignoutMessage);
        signoutChannel.close();
      }
    };
  }, [maybeUpdateSubscriptionData]);

  // Diagnostics: trace authSettled changes
  useEffect(() => {
    try { appendAuthDebugLog({ type: 'authSettled:change', value: authSettled }); } catch { /* no-op */ }
    logger.debug('authSettled changed', { authSettled });
  }, [authSettled]);

  // Diagnostics: trace hardExpired changes
  useEffect(() => {
    try { appendAuthDebugLog({ type: 'hardExpired:change', value: hardExpired }); } catch { /* no-op */ }
    logger.debug('hardExpired changed', { hardExpired });
  }, [hardExpired]);

  // Diagnostics: trace softRecovering changes
  useEffect(() => {
    try { appendAuthDebugLog({ type: 'softRecovering:change', value: softRecovering }); } catch { /* no-op */ }
    logger.debug('softRecovering changed', { softRecovering });
  }, [softRecovering]);

  useEffect(() => {
    const unsubscribe = subscribeEnsureFreshAuth((result) => {
      if (result.phase === 'fail') {
        if (result.reason === 'no_session') {
          // Verify no-session before marking hardExpired
          supabase.auth.getUser().then((quick) => {
            const stillHasUser = !!quick.data?.user?.id;
            appendAuthDebugLog({ type: 'ensureFreshAuth:verify-no_session', stillHasUser, quickUserId: quick.data?.user?.id });
            if (stillHasUser) {
              setSoftRecovering(true);
            } else {
              setHardExpired(true);
              setSoftRecovering(false);
            }
          }).catch(() => {
            setHardExpired(true);
            setSoftRecovering(false);
          });
        } else {
          setSoftRecovering(true);
        }
      }
      if (result.phase === 'success') {
        setHardExpired(false);
        setSoftRecovering(false);
      }
    });
    return unsubscribe;
  }, []);

  // Validate access based on subscription tier - 2-tier system
  const validateAccess = useCallback((requiredTier: string = 'free'): boolean => {
    if (!user) return false;
    
    const tierHierarchy = {
      free: 0,
      premium: 1
    } as const;
    
    const normalizedUserTier = (subscriptionTier === 'premium' ? 'premium' : 'free') as keyof typeof tierHierarchy;
    const normalizedRequiredTier = (requiredTier === 'premium' ? 'premium' : 'free') as keyof typeof tierHierarchy;
    const userTierLevel = tierHierarchy[normalizedUserTier];
    const requiredTierLevel = tierHierarchy[normalizedRequiredTier];
    
    return userTierLevel >= requiredTierLevel;
  }, [user, subscriptionTier]);

  const contextValue = useMemo(() => ({
    user,
    session,
    loading,
    authSettled,
    sessionExpired, // back-compat
    hardExpired,
    softRecovering,
    subscriptionTier,
    isSubscribed,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPasswordForEmail,
    updateEmail,
    validateAccess,
  }), [user, session, loading, authSettled, sessionExpired, hardExpired, softRecovering, subscriptionTier, isSubscribed, signIn, signUp, signInWithGoogle, signOut, resetPasswordForEmail, updateEmail, validateAccess]);

  return (
    <UnifiedAuthContext.Provider value={contextValue}>
      {children}
    </UnifiedAuthContext.Provider>
  );
}
